import React, { useContext } from 'react';
import classNames from 'classnames';

import { media } from 'context';
import { Layout, PillButton, ProductImg, Text } from 'ds';
import AddQuantity from 'ds/AddQuantity';
import { formatUsd } from 'helpers';

import type { ConsumablesProductPickerOption } from '../types';

export interface AIAssistantConsumablesPickerProductCardProps {
  option: ConsumablesProductPickerOption;
  selected: boolean;
  quantity: number;
  maxQty: number;
  currentOrderQuantity: number;
  currentSubscriptionQuantity: number;
  onSelect: () => void;
  onQuantityDelta: (delta: number) => void;
  onQuantityInput: (quantity: number) => void;
}

function existingOrderStatusCopy(subscriptionQty: number, orderQty: number): string | null {
  const hasSub = subscriptionQty > 0;
  const hasOrder = orderQty > 0;
  if (!hasSub && !hasOrder) {
    return null;
  }
  if (hasSub && hasOrder) {
    return `${subscriptionQty} in sub · ${orderQty} in next order`;
  }
  if (hasSub) {
    return `${subscriptionQty} already in subscription`;
  }
  return `${orderQty} already in next order`;
}

const AIAssistantConsumablesPickerProductCard: React.FC<AIAssistantConsumablesPickerProductCardProps> = ({
  option,
  selected,
  quantity,
  maxQty,
  currentOrderQuantity,
  currentSubscriptionQuantity,
  onSelect,
  onQuantityDelta
}) => {
  const { isMobile } = useContext(media);
  const unitCents = Number(option.unit_amount_cents);
  const safeUnitCents = Number.isFinite(unitCents) ? unitCents : 0;
  const cardWidth = isMobile ? 180 : 192;
  const statusCaption = existingOrderStatusCopy(currentSubscriptionQuantity, currentOrderQuantity);

  return (
    <div
      className={classNames('AIAssistantConsumablesPickerProductCard', { 'is-selected': selected })}
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onSelect();
        }
      }}
      style={{ width: cardWidth, minWidth: cardWidth }}
    >
      <Layout __className="AIAssistantConsumablesPickerProductCard-inner" direction="column" height="100%">
        <Layout __className="AIAssistantConsumablesPickerProductCard-imageWrap" position="relative">
          <ProductImg
            srcKey={option.image_key ?? null}
            size="sm"
            alt={option.title || 'Product'}
            style={{ width: '100%', height: '100%', borderRadius: 0, userSelect: 'none' }}
            draggable={false}
          />
        </Layout>
        <Layout __className="AIAssistantConsumablesPickerProductCard-body" direction="column" flexGrow={1}>
          <Layout direction="column" gap={4} flexGrow={1} flexShrink={0}>
            <Text size="body-sm" semibold maxLines={2}>
              {option.title}
            </Text>
            <Text size="body-xs" color="gray-600">
              {formatUsd(safeUnitCents / 100)} ea.
            </Text>
          </Layout>
          <div
            className="AIAssistantConsumablesPickerProductCard-footer"
            onClick={(event: React.MouseEvent<HTMLDivElement>) => event.stopPropagation()}
          >
            <div className="AIAssistantConsumablesPickerProductCard-ctaSlot">
              {selected ? (
                <div className="AIAssistantConsumablesPickerProductCard-addQuantity">
                  <AddQuantity
                    value={quantity}
                    size="sm"
                    minValue={1}
                    maxValue={maxQty}
                    showRemoveItemButton={quantity === 1}
                    handleRemoveItem={() => {
                      onQuantityDelta(-1);
                    }}
                    handleDecreaseQuantity={() => {
                      onQuantityDelta(-1);
                    }}
                    handleIncreaseQuantity={() => {
                      onQuantityDelta(1);
                    }}
                  />
                </div>
              ) : (
                <PillButton
                  type="secondaryOutline"
                  size="md"
                  text="+ Add"
                  fullWidth
                  onClick={() => {
                    onSelect();
                  }}
                />
              )}
            </div>
            {statusCaption ? (
              <Layout __className="AIAssistantConsumablesPickerProductCard-statusCaption" width="100%">
                <Text size="body-xs" color="gray-600" align="center">
                  {statusCaption}
                </Text>
              </Layout>
            ) : null}
          </div>
        </Layout>
      </Layout>
    </div>
  );
};

export default AIAssistantConsumablesPickerProductCard;
